import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Gear, Sparkle, PaperPlaneRight,
  User as UserIcon, Robot,
  CheckCircle, Plus, CaretDown, Trash,
  ChatCircle, Table,
  ArrowLeft, DownloadSimple, UploadSimple,
  XCircle, Warning, Spinner, FileXls,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import WizardFields from "@/components/erp/WizardFields";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function ts() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat → Wizard field name mapping
//
// The conversational agent uses snake_case keys that differ slightly from the
// WizardFields "asset-addition" schema names.  This map bridges both directions.
// ─────────────────────────────────────────────────────────────────────────────

const CHAT_TO_WIZARD = {
  book_type_code:   "book_type",
  asset_type:       "asset_type",
  category_major:   "asset_category",  // step 1 major label
  category_id:      "asset_category",  // step 2 full name overrides major
  category_name:    "asset_category",
  description:      "description",
  cost:             "cost",
  units_assigned:   "units",
  expense_ccid:     "expense_ccid",
  location_country: "location",        // step 1 country label
  location_ccid:    "location",        // step 2 full loc_segments overrides country
  location_name:    "location",
};

// ─────────────────────────────────────────────────────────────────────────────
// Markdown renderer
// ─────────────────────────────────────────────────────────────────────────────

function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p:      ({ node, ...p }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-bold text-gray-900" {...p} />,
          ul:     ({ node, ...p }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...p} />,
          ol:     ({ node, ...p }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...p} />,
          li:     ({ node, ...p }) => <li className="text-sm" {...p} />,
          h3:     ({ node, ...p }) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...p} />,
          code:   ({ node, inline, ...p }) =>
            inline
              ? <code className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded" {...p} />
              : <code className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto" {...p} />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline field UI widgets — shown inside the AI chat bubble
// ─────────────────────────────────────────────────────────────────────────────

/** Table selector for sub-category selection (Minor | Sub-Minor | Future 1) */
function TableSelectOptions({ fieldUi, onPick }) {
  const rows    = fieldUi.rows    || [];
  const columns = fieldUi.columns || [];
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      {/* Header */}
      <div className="grid text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-50 px-3 py-1.5 border-b border-gray-100"
           style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}>
        {columns.map((c) => <span key={c}>{c}</span>)}
      </div>
      {/* Rows */}
      <div className="max-h-52 overflow-y-auto">
        {rows.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No sub-categories found for this Major.</div>
        )}
        {rows.map((row, i) => (
          <div
            key={i}
            onClick={() => onPick({ label: row.label, value: row.value, category_id: row.category_id })}
            className="grid px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
            style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
          >
            {(row.cells || []).map((cell, ci) => (
              <span key={ci} className="text-gray-700 truncate pr-2">{cell}</span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Searchable dropdown (Book Type, Category, Location) */
function SearchableChatOptions({ fieldUi, onPick }) {
  const [q, setQ] = React.useState("");
  const options  = fieldUi.options || [];
  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : options;

  return (
    <div
      className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white"
      data-testid={`chat-field-ui-${fieldUi.field}`}
    >
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${(fieldUi.label || fieldUi.field).replace(/_/g, " ")}…`}
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100"
      />
      <div className="max-h-44 overflow-y-auto">
        {options.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">
            No options available. Check your Oracle connection.
          </div>
        )}
        {options.length > 0 && filtered.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No matches for "{q}"</div>
        )}
        {filtered.slice(0, 60).map((opt) => (
          <div
            key={opt.value}
            onClick={() => onPick(opt)}
            className="px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
          >
            {opt.label}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Radio pills (Asset Type: CAPITALIZED / CIP / EXPENSED, and Confirm/Edit) */
function RadioOptions({ fieldUi, onPick }) {
  const options = fieldUi.options || [];
  return (
    <div
      className="mt-2 flex flex-wrap gap-1.5"
      data-testid={`chat-field-ui-${fieldUi.field}`}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onPick(opt)}
          className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors"
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat bubble
// ─────────────────────────────────────────────────────────────────────────────

function AssetChatBubble({ msg, onOptionClick, isLastAssistant }) {
  const isUser  = msg.role === "user";
  // Only render the interactive field UI widget on the LAST assistant message.
  // Earlier messages had their field answered — their widgets are now stale.
  const fieldUi = !isUser && !msg.loading && isLastAssistant
    ? msg.result?.next_field_ui
    : null;
  const uiType  = fieldUi?.uiType;

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      {/* Avatar */}
      <div
        className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white
          ${isUser ? "bg-[#0020C2]" : "bg-gray-700"}`}
      >
        {isUser ? <UserIcon size={11} weight="bold" /> : <Robot size={11} weight="bold" />}
      </div>

      {/* Bubble */}
      <div
        className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed
          ${isUser
            ? "bg-[#0020C2] text-white rounded-tr-sm"
            : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm"
          }`}
      >
        {/* Loading dots */}
        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map((d) => (
              <span
                key={d}
                className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: `${d}ms` }}
              />
            ))}
          </div>
        )}

        {/* Text */}
        {!msg.loading && msg.text && (
          isUser
            ? <p className="whitespace-pre-wrap">{msg.text}</p>
            : <ChatMarkdown>{msg.text}</ChatMarkdown>
        )}

        {/* Inline field UI — only one at a time, only in the current AI bubble */}
        {uiType === "searchable_dropdown" && (
          <SearchableChatOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {uiType === "radio" && (
          <RadioOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {uiType === "table_select" && (
          <TableSelectOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {/* Fallback plain buttons */}
        {fieldUi &&
          !["searchable_dropdown", "radio"].includes(uiType) &&
          fieldUi.options?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`chat-field-ui-${fieldUi.field}`}>
              {fieldUi.options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => onOptionClick?.(opt)}
                  className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>
            {msg.timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Progress bar
// ─────────────────────────────────────────────────────────────────────────────

function ProgressBar({ progress }) {
  if (!progress) return null;
  const pctMatch  = progress.match(/\((\d+)%\)/);
  const stepMatch = progress.match(/(\d+)\/(\d+)/);
  if (!pctMatch || !stepMatch) return null;
  const pct       = parseInt(pctMatch[1], 10);
  const [, done, total] = stepMatch;
  return (
    <div className="px-4 pt-3 pb-1">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>Step {done} of {total}</span>
        <span>{pct}% complete</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#0020C2] transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Input bar
// ─────────────────────────────────────────────────────────────────────────────

function AssetInputBar({ inputRef, value, onChange, onKeyDown, onSend, busy }) {
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div
        className="flex items-end gap-2 border border-gray-300 bg-gray-50
          focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100"
      >
        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder="Continue the conversation…"
          data-testid="agent-prompt"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none outline-none
            placeholder-gray-400 disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }}
        />
        <button
          onClick={onSend}
          disabled={busy || !value.trim()}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center
            hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0"
        >
          {busy
            ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">↵ send · shift+↵ new line</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// New Chat button
// ─────────────────────────────────────────────────────────────────────────────

function ChatHistoryControl({ onNewChat }) {
  return (
    <button
      onClick={onNewChat}
      className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1 shrink-0"
      data-testid="chat-new"
      title="Start a new conversation"
    >
      <Plus size={13} weight="bold" /> New Chat
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Success state
// ─────────────────────────────────────────────────────────────────────────────

function SuccessBanner({ reply, onClose }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 bg-gray-50">
      <div className="w-16 h-16 rounded-full bg-[#0020C2]/10 flex items-center justify-center mb-5">
        <CheckCircle size={36} weight="fill" className="text-[#0020C2]" />
      </div>
      <h2 className="text-xl font-black text-gray-900 mb-2">Asset Created!</h2>
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <ChatMarkdown>{reply}</ChatMarkdown>
      </div>
      <button
        onClick={onClose}
        className="mt-6 px-6 py-2 rounded-lg text-sm font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] transition-colors"
      >
        Close
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── BULK UPLOAD COMPONENTS ───────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

const BULK_PREVIEW_COLS = [
  { key: "book_type_code",         label: "Book Type" },
  { key: "asset_type",             label: "Type" },
  { key: "category_name",          label: "Category" },
  { key: "description",            label: "Description" },
  { key: "date_placed_in_service", label: "Date" },
  { key: "cost",                   label: "Cost" },
  { key: "units_assigned",         label: "Units" },
  { key: "location_name",          label: "Location" },
];

function BulkStatusBadge({ valid }) {
  return valid ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">
      <CheckCircle size={10} weight="fill" /> Valid
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">
      <XCircle size={10} weight="fill" /> Errors
    </span>
  );
}

function BulkUploadZone({ onFileSelected, onDownloadTemplate, downloading }) {
  const inputRef  = React.useRef(null);
  const [drag, setDrag] = React.useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) { alert("Only .xlsx or .xls files are accepted."); return; }
    onFileSelected(file);
  };

  return (
    <div className="flex flex-col items-center gap-6 py-10 px-8">
      {/* Template download banner */}
      <div className="w-full max-w-md bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <FileXls size={28} weight="duotone" className="text-[#0020C2] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">Step 1 — Download Template</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Pre-filled with Oracle dropdowns for Book Types, Categories and Locations.
          </p>
        </div>
        <button
          onClick={onDownloadTemplate}
          disabled={downloading}
          className="shrink-0 flex items-center gap-1.5 text-xs font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] disabled:opacity-50 px-3 py-2 rounded-lg transition-colors"
        >
          {downloading ? <Spinner size={13} className="animate-spin" /> : <DownloadSimple size={13} weight="bold" />}
          Download
        </button>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`w-full max-w-md border-2 border-dashed rounded-xl p-10 flex flex-col items-center gap-3 cursor-pointer transition-colors
          ${drag ? "border-[#0020C2] bg-blue-50" : "border-gray-300 hover:border-[#0020C2] hover:bg-gray-50"}`}
      >
        <UploadSimple size={36} weight="duotone" className="text-gray-400" />
        <p className="text-sm font-semibold text-gray-700">Step 2 — Drop your filled Excel here</p>
        <p className="text-xs text-gray-400">or click to browse (.xlsx, .xls)</p>
        <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
      </div>

      <p className="text-xs text-gray-400 text-center max-w-md">
        All rows are validated against Oracle before anything is submitted.
      </p>
    </div>
  );
}

function BulkPreviewTable({ rows }) {
  return (
    <div className="overflow-auto border border-gray-200 rounded-xl">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 w-12">Row</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 w-20">Status</th>
            {BULK_PREVIEW_COLS.map((c) => (
              <th key={c.key} className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 whitespace-nowrap">{c.label}</th>
            ))}
            <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200">Errors</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.row_num} className={row.valid ? "hover:bg-gray-50" : "bg-red-50 hover:bg-red-100"}>
              <td className="px-3 py-2 border-b border-gray-100 font-mono text-gray-500">{row.row_num}</td>
              <td className="px-3 py-2 border-b border-gray-100"><BulkStatusBadge valid={row.valid} /></td>
              {BULK_PREVIEW_COLS.map((c) => (
                <td key={c.key} className="px-3 py-2 border-b border-gray-100 text-gray-700 max-w-[140px] truncate" title={row.data?.[c.key] || ""}>
                  {row.data?.[c.key] || <span className="text-gray-300">—</span>}
                </td>
              ))}
              <td className="px-3 py-2 border-b border-gray-100">
                {row.errors?.length > 0 ? (
                  <ul className="space-y-0.5">
                    {row.errors.map((e, i) => (
                      <li key={i} className="text-red-600 flex items-start gap-1">
                        <XCircle size={10} weight="fill" className="shrink-0 mt-0.5" /> {e}
                      </li>
                    ))}
                  </ul>
                ) : <span className="text-green-600">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk upload flow — self-contained, owns its own state
// ─────────────────────────────────────────────────────────────────────────────

const BULK_STAGE = { UPLOAD: "UPLOAD", VALIDATING: "VALIDATING", PREVIEW: "PREVIEW", SUBMITTING: "SUBMITTING", SUMMARY: "SUMMARY" };

function AssetBulkFlow({ onBack, onClose }) {
  const { setOpenAgent, setSetupOpen, profile } = useErp();
  const [stage,       setStage]       = React.useState(BULK_STAGE.UPLOAD);
  const [downloading, setDownloading] = React.useState(false);
  const [fileName,    setFileName]    = React.useState("");
  const [validation,  setValidation]  = React.useState(null);
  const [submitResult,setSubmitResult]= React.useState(null);
  const [submitting,  setSubmitting]  = React.useState(false);
  const [error,       setError]       = React.useState("");

  const close = () => { setOpenAgent(null); onClose?.(); };

  const handleDownloadTemplate = async () => {
    setDownloading(true); setError("");
    try {
      const resp = await api.get("/erp-agents/asset-create/bulk/template", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "asset_bulk_upload_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Template download failed: " + (e?.response?.data?.detail || e.message));
    } finally { setDownloading(false); }
  };

  const handleFileSelected = async (file) => {
    setFileName(file.name); setError(""); setStage(BULK_STAGE.VALIDATING);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/erp-agents/asset-create/bulk/validate-file", form, { headers: { "Content-Type": "multipart/form-data" } });
      setValidation(data); setStage(BULK_STAGE.PREVIEW);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Validation failed.");
      setStage(BULK_STAGE.UPLOAD);
    }
  };

  const handleConfirm = async () => {
    if (!validation) return;
    const validRows = validation.rows.filter((r) => r.valid);
    if (!validRows.length) return;
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/erp-agents/asset-create/bulk/submit", { rows: validRows });
      setSubmitResult(data); setStage(BULK_STAGE.SUMMARY);
    } catch (e) {
      setError("Submission failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  const handleReupload = () => { setValidation(null); setFileName(""); setError(""); setStage(BULK_STAGE.UPLOAD); };

  const isWide = stage === BULK_STAGE.PREVIEW || stage === BULK_STAGE.SUMMARY;

  return (
    <>
      <style>{`
        @keyframes bulkFadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .bulk-modal-narrow { width:640px; max-width:calc(100vw - 2rem); max-height:90vh; animation:bulkFadeIn 0.3s ease; }
        .bulk-modal-wide   { width:min(92vw,1300px); height:90vh; animation:bulkFadeIn 0.3s ease; }
      `}</style>
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={close}>
        <div onClick={(e) => e.stopPropagation()}
          className={`bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden shadow-2xl ${isWide ? "bulk-modal-wide" : "bulk-modal-narrow"}`}
          style={{ transition: "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Oracle Fusion ERP · Vaku AI</div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5">Asset Addition — Bulk Upload</h3>
              <p className="text-xs text-gray-500 mt-0.5">Create multiple assets via Excel</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {stage === BULK_STAGE.UPLOAD && (
                <button onClick={onBack} className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
                  <ArrowLeft size={13} weight="bold" /> Single asset
                </button>
              )}
              <button onClick={() => { close(); setSetupOpen?.(true); }} className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div className="mx-5 mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 shrink-0">
              <XCircle size={14} weight="fill" className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {stage === BULK_STAGE.UPLOAD && (
              <BulkUploadZone onFileSelected={handleFileSelected} onDownloadTemplate={handleDownloadTemplate} downloading={downloading} />
            )}

            {stage === BULK_STAGE.VALIDATING && (
              <div className="flex flex-col items-center justify-center gap-4 py-16">
                <Spinner size={36} className="animate-spin text-[#0020C2]" />
                <p className="text-sm font-semibold text-gray-700">Validating {fileName}…</p>
                <p className="text-xs text-gray-400">Checking all rows against Oracle LOV data</p>
              </div>
            )}

            {stage === BULK_STAGE.PREVIEW && validation && (
              <div className="flex flex-col gap-4 p-5 h-full overflow-hidden">
                {/* Summary */}
                <div className="flex items-center gap-4 shrink-0">
                  <div className="flex-1 text-sm">
                    <span className="font-bold text-gray-900">{fileName}</span>
                    <span className="text-gray-500 ml-2">— {validation.total_rows} row{validation.total_rows !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs font-semibold">
                    <span className="text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">✓ {validation.valid_rows} valid</span>
                    {validation.invalid_rows > 0 && <span className="text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">✗ {validation.invalid_rows} invalid</span>}
                  </div>
                </div>
                {validation.invalid_rows > 0 && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 shrink-0">
                    <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                      {validation.invalid_rows} row{validation.invalid_rows !== 1 ? "s" : ""} will be skipped.
                      {validation.valid_rows > 0 ? ` Only the ${validation.valid_rows} valid row${validation.valid_rows !== 1 ? "s" : ""} will be submitted.` : " Fix the errors and re-upload."}
                    </p>
                  </div>
                )}
                <div className="flex-1 overflow-auto min-h-0"><BulkPreviewTable rows={validation.rows} /></div>
                <div className="flex items-center justify-between shrink-0 pt-2 border-t border-gray-200">
                  <button onClick={handleReupload} disabled={submitting} className="text-xs font-semibold text-gray-500 hover:text-gray-800 flex items-center gap-1.5 disabled:opacity-50">
                    <UploadSimple size={13} /> Upload different file
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={!validation.valid_rows || submitting}
                    className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {submitting ? <><Spinner size={14} className="animate-spin" /> Submitting…</> : `Submit ${validation.valid_rows} Asset${validation.valid_rows !== 1 ? "s" : ""} to Oracle`}
                  </button>
                </div>
              </div>
            )}

            {stage === BULK_STAGE.SUMMARY && submitResult && (
              <div className="flex flex-col gap-5 p-6 h-full overflow-hidden">
                <div className="flex flex-col items-center gap-3 py-4 shrink-0">
                  {submitResult.failed === 0
                    ? <CheckCircle size={48} weight="fill" className="text-[#0020C2]" />
                    : <Warning size={48} weight="fill" className="text-amber-500" />}
                  <h2 className="text-xl font-black text-gray-900">{submitResult.failed === 0 ? "Bulk Upload Complete!" : "Completed with Errors"}</h2>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="font-semibold text-green-700">{submitResult.succeeded} succeeded</span>
                    {submitResult.failed > 0 && <span className="font-semibold text-red-700">{submitResult.failed} failed</span>}
                    <span className="text-gray-400">(of {submitResult.submitted} submitted)</span>
                  </div>
                </div>
                <div className="flex-1 overflow-auto min-h-0 border border-gray-200 rounded-xl">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200 w-14">Row</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200">Result</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-500 border-b border-gray-200">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submitResult.results.map((r) => (
                        <tr key={r.row_num} className={r.success ? "hover:bg-gray-50" : "bg-red-50 hover:bg-red-100"}>
                          <td className="px-3 py-2 border-b border-gray-100 font-mono text-gray-500">{r.row_num}</td>
                          <td className="px-3 py-2 border-b border-gray-100">
                            {r.success
                              ? <span className="flex items-center gap-1 text-green-700 font-semibold"><CheckCircle size={12} weight="fill" /> Created</span>
                              : <span className="flex items-center gap-1 text-red-700 font-semibold"><XCircle size={12} weight="fill" /> Failed</span>}
                          </td>
                          <td className="px-3 py-2 border-b border-gray-100 text-gray-600">
                            {r.success ? "Asset created successfully" : (r.error || "Oracle returned failure")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between shrink-0 pt-2 border-t border-gray-200">
                  <button onClick={handleReupload} className="text-xs font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1.5">
                    <UploadSimple size={13} /> Upload another file
                  </button>
                  <button onClick={close} className="px-5 py-2 text-sm font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] rounded-lg transition-colors">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mode-selection screen
// ─────────────────────────────────────────────────────────────────────────────

function ModeSelectScreen({ onSingle, onBulk, onClose, agent, profile, setSetupOpen }) {
  return (
    <>
      <style>{`
        @keyframes modeSelectFadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .mode-modal { width:560px; max-width:calc(100vw - 2rem); animation:modeSelectFadeIn 0.3s ease; }
      `}</style>
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()} className="bg-white text-gray-900 border border-gray-300 flex flex-col shadow-2xl mode-modal">
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Oracle Fusion ERP · Vaku AI</div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5">{agent.label}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{agent.hint}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button onClick={() => { onClose(); setSetupOpen?.(true); }} className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
          </div>
          <div className="p-6 flex flex-col gap-4">
            <p className="text-sm text-gray-600 font-medium">How would you like to create assets?</p>
            <div className="grid grid-cols-2 gap-4">
              <button onClick={onSingle} className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-xl hover:border-[#0020C2] hover:bg-blue-50 transition-all group">
                <div className="w-12 h-12 rounded-full bg-gray-100 group-hover:bg-[#0020C2]/10 flex items-center justify-center transition-colors">
                  <ChatCircle size={24} weight="duotone" className="text-gray-500 group-hover:text-[#0020C2]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 text-center">Single Asset</p>
                  <p className="text-xs text-gray-500 mt-1 text-center">Chat-guided flow, one asset at a time</p>
                </div>
              </button>
              <button onClick={onBulk} className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-xl hover:border-[#0020C2] hover:bg-blue-50 transition-all group">
                <div className="w-12 h-12 rounded-full bg-gray-100 group-hover:bg-[#0020C2]/10 flex items-center justify-center transition-colors">
                  <Table size={24} weight="duotone" className="text-gray-500 group-hover:text-[#0020C2]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 text-center">Bulk Upload</p>
                  <p className="text-xs text-gray-500 mt-1 text-center">Upload Excel to create multiple assets at once</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Main modal — top-level entry point
// Handles mode selection; delegates to AssetSingleFlow or AssetBulkFlow
// ─────────────────────────────────────────────────────────────────────────────

export default function AssetAgentModal({ onClose }) {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents } = useErp();
  const agent = agents?.find((a) => a.key === openAgent) || { label: "Asset Addition", hint: "Create a new fixed asset in Oracle Fusion" };
  const [mode, setMode] = React.useState(null); // null | "single" | "bulk"
  const close = () => { setOpenAgent(null); onClose?.(); };

  if (mode === "bulk")   return <AssetBulkFlow onBack={() => setMode(null)} onClose={onClose} />;
  if (mode === "single") return <AssetSingleFlow onClose={onClose} />;

  return (
    <ModeSelectScreen
      onSingle={() => setMode("single")}
      onBulk={() => setMode("bulk")}
      onClose={close}
      agent={agent}
      profile={profile}
      setSetupOpen={setSetupOpen}
    />
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Original single-asset flow (UNCHANGED — all hooks always called here)
// ─────────────────────────────────────────────────────────────────────────────

function AssetSingleFlow({ onClose }) {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents } = useErp();

  const agent = agents?.find((a) => a.key === openAgent) || {
    label: "Asset Addition",
    hint:  "Create a new fixed asset in Oracle Fusion",
  };

  // Session persisted in sessionStorage so re-opening the modal resumes
  const SESSION_KEY = "asset_session_id";
  const [sessionId, setSessionId] = React.useState(() => {
    // Return whatever is in sessionStorage (may be empty string if no session yet).
    // The real session_id is set by doInit() once /start responds.
    return sessionStorage.getItem(SESSION_KEY) || "";
  });

  const [messages,      setMessages]      = React.useState([]);
  const [input,         setInput]         = React.useState("");
  const [busy,          setBusy]          = React.useState(false);
  const [progress,      setProgress]      = React.useState(null);
  const [assetCreated,  setAssetCreated]  = React.useState(false);
  const [successReply,  setSuccessReply]  = React.useState("");
  const [started,       setStarted]       = React.useState(false);

  // ── Wizard panel state ───────────────────────────────────────────────────
  // fieldValues mirrors the WizardFields "asset-addition" schema names.
  const [fieldValues,   setFieldValues]   = React.useState({});
  const [missingFields, setMissingFields] = React.useState([]);
  // chatStarted controls whether we show the 60/40 split layout
  const [chatStarted,   setChatStarted]   = React.useState(false);

  const messagesEndRef = React.useRef(null);
  const inputRef       = React.useRef(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Initialize session on modal open ────────────────────────────────────────
  //
  // CRITICAL STATE RULE:
  //   /start creates a NEW backend session and generates the greeting.
  //   It must be called ONLY ONCE per logical Asset Creation flow.
  //
  //   If sessionStorage already contains a session_id the user has an active
  //   session → skip /start, show existing messages, and prompt the next field.
  //   This prevents the double-greeting bug where the second mount (or first
  //   chat message with "Hi") triggered a fresh greeting + book options again.
  React.useEffect(() => {
    if (started) return;
    setStarted(true);

    const doInit = async () => {
      setBusy(true);
      const existingSid = sessionStorage.getItem(SESSION_KEY);

      if (existingSid) {
        // Resume existing session — do NOT call /start again.
        // The session lives in Redis; the next /chat will load it correctly.
        // Start with an empty chat — the user's first message will drive the flow.
        // If the user types "Hi", the backend will respond with the next required field.
        setSessionId(existingSid);
        setBusy(false);
        setTimeout(() => inputRef.current?.focus(), 150);
        return;
      }

      // No existing session → call /start to create the session and prefetch LOVs.
      // No greeting message is shown — the conversation starts empty.
      // The greeting is generated when the user sends their first message.
      try {
        const { data } = await api.post("/erp-agents/asset-create/start");
        const sid = data.session_id;
        sessionStorage.setItem(SESSION_KEY, sid);
        setSessionId(sid);
        // Leave messages empty — user types first
      } catch (e) {
        // /start failed — log it; user can still type and /chat will create state
        console.warn("[AssetAgent] /start failed:", e?.response?.data?.detail || e.message);
      } finally {
        setBusy(false);
        setTimeout(() => inputRef.current?.focus(), 150);
      }
    };

    doInit();
  }, [started]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync chat fields_collected → wizard fieldValues ──────────────────────
  //
  // Rules:
  //  1. Only map keys explicitly in CHAT_TO_WIZARD — unknown keys are ignored
  //     so raw numeric IDs never bleed into the wrong wizard field.
  //  2. category_name / location_name take precedence over the raw IDs.
  //  3. Never overwrite a wizard field that already has a value.
  const syncFieldsToWizard = React.useCallback((fieldsCollected) => {
    if (!fieldsCollected || typeof fieldsCollected !== "object") return;
    setFieldValues((prev) => {
      const merged = { ...prev };

      // Pass 1: human-readable names first (highest priority)
      if (fieldsCollected.category_name) {
        merged["asset_category"] = String(fieldsCollected.category_name);
      }
      if (fieldsCollected.location_name) {
        merged["location"] = String(fieldsCollected.location_name);
      }

      // Pass 2: all other mapped fields — skip raw ID keys and already-set fields
      for (const [chatKey, val] of Object.entries(fieldsCollected)) {
        if (val === null || val === undefined || val === "") continue;
        // Raw ID / display-name fields handled above
        if (["category_id", "category_name", "location_ccid", "location_name"].includes(chatKey)) continue;
        // Only map keys that exist in CHAT_TO_WIZARD — never guess
        const wizardKey = CHAT_TO_WIZARD[chatKey];
        if (!wizardKey) continue;
        // Never overwrite an already-set field
        if (merged[wizardKey] !== undefined && merged[wizardKey] !== "") continue;
        merged[wizardKey] = String(val);
      }

      return merged;
    });
  }, []);

  // ── Sync wizard field changes → agent session via /set-fields ────────────
  // When the user picks a Category from the wizard dropdown, also call
  // /category-expense to auto-resolve the Expense Account CCID (mirrors
  // Oracle's own "Add Asset" behaviour — user never types the CCID).
  const handleWizardChange = React.useCallback(async (newValues) => {
    setFieldValues((prev) => {
      // Detect category change so we can trigger expense_ccid auto-resolve below
      handleWizardChange._prevCategory = prev.asset_category;
      return newValues;
    });

    // Map wizard keys back to agent state field names for /set-fields
    const WIZARD_TO_AGENT = {
      book_type:      "book_type_code",
      asset_type:     "asset_type",
      asset_category: "category_name",
      description:    "description",
      cost:           "cost",
      units:          "units_assigned",
      // expense_ccid is auto-derived from category — not user-editable
      location:       "location_name",
    };

    const agentFields = {};
    for (const [wizKey, agentKey] of Object.entries(WIZARD_TO_AGENT)) {
      const val = newValues[wizKey];
      if (val !== undefined && val !== null && String(val).trim() !== "") {
        agentFields[agentKey] = String(val);
      }
    }

    if (Object.keys(agentFields).length > 0) {
      try {
        await api.post("/erp-agents/asset-create/set-fields", {
          session_id: sessionId,
          fields:     agentFields,
        });
      } catch (e) {
        console.warn("[AssetAgent] set-fields sync failed:", e?.response?.data?.detail || e.message);
      }
    }

    // Auto-resolve Expense Account when category is picked or changes
    const newCategory    = newValues.asset_category;
    const prevCategory   = handleWizardChange._prevCategory;
    const categoryChanged = newCategory && newCategory !== prevCategory;

    if (categoryChanged) {
      try {
        const { data } = await api.post("/erp-agents/asset-create/category-expense", {
          session_id: sessionId,
          category:   newCategory,
        });
        if (data.expense_ccid) {
          setFieldValues((prev) => ({ ...prev, expense_ccid: data.expense_ccid }));
        }
      } catch (e) {
        console.warn("[AssetAgent] category-expense lookup failed:", e?.response?.data?.detail || e.message);
      }
    }
  }, [sessionId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Send a user message ──────────────────────────────────────────────────
  const sendMessage = async (text) => {
    const userText = (text || "").trim();
    if (!userText || busy) return;

    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (!chatStarted) setChatStarted(true);

    const now = ts();
    setMessages((prev) => [
      ...prev,
      { id: Date.now(),     role: "user",      text: userText, timestamp: now },
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);

    try {
      const { data } = await api.post("/erp-agents/asset-create/chat", {
        session_id: sessionId,
        message:    userText,
      });

      if (data.progress) setProgress(data.progress);

      // Sync collected fields into the wizard panel
      if (data.fields_collected) {
        syncFieldsToWizard(data.fields_collected);
      }

      if (data.asset_created) {
        setAssetCreated(true);
        setSuccessReply(data.reply || "Asset created successfully!");
      }

      const reply = data.reply || data.response || data.message || "Done.";
      const now2  = ts();
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, text: reply, result: data, timestamp: now2 }
            : m
        )
      );
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e.message || "An error occurred.";
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, timestamp: ts(), text: `❌ ${errMsg}` }
            : m
        )
      );
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  // ── New chat — clear session and call /start fresh ───────────────────────
  const startNewChat = () => {
    // Clear sessionStorage so the next useEffect run calls /start for a new session
    sessionStorage.removeItem(SESSION_KEY);
    setSessionId("");
    setMessages([]);
    setInput("");
    setProgress(null);
    setAssetCreated(false);
    setSuccessReply("");
    setStarted(false);      // triggers useEffect → /start (no existing session found)
    setChatStarted(false);
    setFieldValues({});
    setMissingFields([]);
  };

  // Closing the modal always starts fresh — the user explicitly dismissed it.
  // Wiping sessionStorage means next open creates a new session + new greeting.
  // This fixes scenario 1.1 (close → reopen shows greeting, not old state)
  // and scenario 1.4 (New Chat also already clears everything).
  const close = () => {
    sessionStorage.removeItem(SESSION_KEY);   // force fresh session on next open
    setOpenAgent(null);
    onClose?.();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes assetChatFadeIn   { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes assetWizardSlide  { from { opacity:0; transform:translateX(20px);  } to { opacity:1; transform:translateX(0); } }
        @keyframes assetFadeIn       { from { opacity:0; transform:translateY(8px);   } to { opacity:1; transform:translateY(0); } }
        .asset-modal-pre  { width: 640px; max-width: calc(100vw - 2rem); max-height: 90vh; animation: assetFadeIn 0.3s ease; }
        .asset-modal-chat { width: min(92vw, 1400px); height: 90vh; animation: assetFadeIn 0.3s ease; }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={close}
      >
        {/* Modal — expands from pre-chat width to full split layout when chat starts */}
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid={`agent-modal-${openAgent}`}
          className={`bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden shadow-2xl
            ${chatStarted ? "asset-modal-chat" : "asset-modal-pre"}`}
          style={{ transition: "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}
        >
          {/* ── Header ── */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle Fusion ERP · Vaku AI
              </div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5">
                {agent.label}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{agent.hint}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {chatStarted && (
                <>
                  <ChatHistoryControl onNewChat={startNewChat} />
                  <div className="w-px h-5 bg-gray-200" />
                </>
              )}
              <button
                onClick={() => { close(); setSetupOpen?.(true); }}
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"
              >
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} data-testid="agent-modal-close" className="text-gray-400 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          {assetCreated ? (
            <SuccessBanner reply={successReply} onClose={close} />

          ) : !chatStarted ? (

            /* ── Pre-chat: wizard + input (narrow modal) ── */
            <>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <WizardFields
                  agentKey="asset-addition"
                  values={fieldValues}
                  onChange={handleWizardChange}
                  missingFields={missingFields}
                  onCreateNew={() => {}}
                  fieldErrors={{}}
                  fieldWarnings={{}}
                />
              </div>
              <AssetInputBar
                inputRef={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSend={() => sendMessage(input)}
                busy={busy}
              />
            </>

          ) : (

            /* ── Chat mode: 60% chat / 40% wizard ── */
            <div className="flex flex-1 overflow-hidden">

              {/* LEFT — chat panel */}
              <div
                className="flex flex-col overflow-hidden border-r border-gray-200"
                style={{ width: "60%", animation: "assetChatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}
              >
                <ProgressBar progress={progress} />

                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                  {messages.map((msg) => {
                    // Only show the inline field dropdown on the LAST non-loading
                    // assistant message. All earlier messages had their field answered
                    // — showing stale dropdowns confuses users into re-submitting them.
                    const lastAssistantId = [...messages]
                      .reverse()
                      .find((m) => m.role === "assistant" && !m.loading)?.id;
                    const isLast = msg.role === "assistant" && msg.id === lastAssistantId;
                    return (
                      <AssetChatBubble
                        key={msg.id}
                        msg={msg}
                        isLastAssistant={isLast}
                        onOptionClick={(opt) => sendMessage(opt.value)}
                      />
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                <AssetInputBar
                  inputRef={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onSend={() => sendMessage(input)}
                  busy={busy}
                />
              </div>

              {/* RIGHT — wizard panel (auto-filled as the conversation progresses) */}
              <div
                className="flex flex-col bg-white"
                style={{ width: "40%", minWidth: "360px", animation: "assetWizardSlide 0.45s cubic-bezier(0.4,0,0.2,1)" }}
              >
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
                    <Sparkle size={11} weight="fill" /> Wizard fields
                    <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">
                      · auto-filled as you chat
                    </span>
                  </div>
                  <WizardFields
                    agentKey="asset-addition"
                    values={fieldValues}
                    onChange={handleWizardChange}
                    missingFields={missingFields}
                    onCreateNew={() => {}}
                    fieldErrors={{}}
                    fieldWarnings={{}}
                  />
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </>
  );
}